/**
 * Per-page <head> metadata, built from the `seo` field of each route in
 * utils/constants/routes.js.
 *
 *   getPageHead(pathname)  -> head model (title, description, canonical, robots, Open Graph, JSON-LD)
 *   renderHeadTags(head)   -> HTML string, used by the build-time prerenderer
 *   applyHead(head)        -> updates document.head after client-side navigation
 */
import routes from 'utils/constants/routes'
import { faq, products } from 'utils/constants/product'
import { IS_PRODUCTION } from 'config'

import { DEFAULT_IMAGE, SITE_NAME, THEME_COLOR, absoluteUrl } from './site'
import {
    breadcrumbSchema,
    faqSchema,
    organizationSchema,
    softwareApplicationSchema,
    websiteSchema,
} from './structuredData'

export const NOT_FOUND_PATH = '*'

const normalisePath = (pathname = '/') => {
    const path = pathname.split(/[?#]/)[0] || '/'
    return path.length > 1 ? path.replace(/\/+$/, '') : path
}

export function findRoute(pathname) {
    const path = normalisePath(pathname)
    return routes.find((route) => route.path === path) || routes.find((route) => route.path === NOT_FOUND_PATH)
}

const productDescription = (product) => {
    const route = routes.find((r) => r.path === `/product/${product.slug}`)
    return (route && route.seo.description) || product.description
}

function structuredDataFor(route, seo) {
    if (route.path === NOT_FOUND_PATH) return []
    const data = [organizationSchema()]
    if (route.path === '/') {
        data.push(websiteSchema())
        return data
    }
    data.push(breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: seo.title, path: route.path },
    ]))
    if (seo.faq) data.push(faqSchema(faq))
    if (seo.pricing) products.forEach((product) => data.push(softwareApplicationSchema(product, productDescription(product))))
    if (seo.product) data.push(softwareApplicationSchema(seo.product, seo.description))
    return data.filter(Boolean)
}

export function getPageHead(pathname) {
    const route = findRoute(pathname)
    const seo = route.seo || {}
    const isNotFound = route.path === NOT_FOUND_PATH
    const noindex = !IS_PRODUCTION || Boolean(seo.noindex)
    const title = seo.fullTitle || `${seo.title} | ${SITE_NAME}`
    const url = isNotFound ? null : absoluteUrl(route.path)
    return {
        path: route.path,
        status: isNotFound ? 404 : 200,
        title,
        description: seo.description,
        canonical: url,
        robots: noindex
            ? (IS_PRODUCTION ? 'noindex, follow' : 'noindex, nofollow')
            : 'index, follow, max-image-preview:large',
        indexable: !noindex && !isNotFound,
        themeColor: THEME_COLOR,
        og: {
            type: 'website',
            siteName: SITE_NAME,
            title,
            description: seo.description,
            url,
            image: absoluteUrl(DEFAULT_IMAGE.path),
            imageWidth: DEFAULT_IMAGE.width,
            imageHeight: DEFAULT_IMAGE.height,
            imageAlt: DEFAULT_IMAGE.alt,
            locale: 'en_US',
        },
        jsonLd: structuredDataFor(route, seo),
    }
}

/** [tag, keyAttribute, keyValue, contentAttribute, content] for every managed meta/link tag. */
function headEntries(head) {
    const { og } = head
    return [
        ['meta', 'name', 'description', 'content', head.description],
        ['meta', 'name', 'robots', 'content', head.robots],
        ['link', 'rel', 'canonical', 'href', head.canonical],
        ['meta', 'property', 'og:type', 'content', og.type],
        ['meta', 'property', 'og:site_name', 'content', og.siteName],
        ['meta', 'property', 'og:locale', 'content', og.locale],
        ['meta', 'property', 'og:title', 'content', og.title],
        ['meta', 'property', 'og:description', 'content', og.description],
        ['meta', 'property', 'og:url', 'content', og.url],
        ['meta', 'property', 'og:image', 'content', og.image],
        ['meta', 'property', 'og:image:width', 'content', String(og.imageWidth)],
        ['meta', 'property', 'og:image:height', 'content', String(og.imageHeight)],
        ['meta', 'property', 'og:image:alt', 'content', og.imageAlt],
        ['meta', 'name', 'twitter:card', 'content', 'summary_large_image'],
        ['meta', 'name', 'twitter:title', 'content', og.title],
        ['meta', 'name', 'twitter:description', 'content', og.description],
        ['meta', 'name', 'twitter:image', 'content', og.image],
        ['meta', 'name', 'twitter:image:alt', 'content', og.imageAlt],
    ]
}

const escapeHtml = (value) =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

// JSON inside <script>: escape "<" so "</script>" can never close the tag.
const jsonForScript = (value) => JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029')

export function renderHeadTags(head) {
    const tags = [`<title>${escapeHtml(head.title)}</title>`]
    headEntries(head).forEach(([tag, keyAttr, key, valueAttr, value]) => {
        if (value === null || value === undefined) return
        tags.push(`<${tag} ${keyAttr}="${key}" ${valueAttr}="${escapeHtml(value)}">`)
    })
    head.jsonLd.forEach((data) => {
        tags.push(`<script type="application/ld+json">${jsonForScript(data)}</script>`)
    })
    return tags.join('')
}

export function applyHead(head, doc = document) {
    doc.title = head.title
    headEntries(head).forEach(([tag, keyAttr, key, valueAttr, value]) => {
        let el = doc.head.querySelector(`${tag}[${keyAttr}="${key}"]`)
        if (value === null || value === undefined) {
            if (el) el.remove()
            return
        }
        if (!el) {
            el = doc.createElement(tag)
            el.setAttribute(keyAttr, key)
            doc.head.appendChild(el)
        }
        if (el.getAttribute(valueAttr) !== value) el.setAttribute(valueAttr, value)
    })
    const json = head.jsonLd.map((data) => JSON.stringify(data))
    const current = Array.from(doc.head.querySelectorAll('script[type="application/ld+json"]'))
    const sameJson = (el, i) => {
        try {
            return JSON.stringify(JSON.parse(el.textContent)) === json[i]
        } catch (e) {
            return false
        }
    }
    if (current.length === json.length && current.every(sameJson)) return
    current.forEach((el) => el.remove())
    json.forEach((text) => {
        const el = doc.createElement('script')
        el.type = 'application/ld+json'
        el.textContent = text
        doc.head.appendChild(el)
    })
}
